// File: src/components/layout/Header.tsx
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import { toggleLocalePathname, useLocalizedPath } from '../../i18n/localeRouting';
import { ThemeToggle } from '../ThemeToggle';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { locale, setLocale, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const lp = useLocalizedPath();

  const onChangeLocale = (next: 'en' | 'es') => {
    const nextPath = toggleLocalePathname(location.pathname, next);
    setLocale(next);
    navigate(nextPath);
  };

  return (
    <header className="bg-surface/80 backdrop-blur-theme border-b border-subtle/60 sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Branding */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-2">
              <Link to={lp('/')}>
                <img className="h-12 w-auto" src="/logo.png" alt="Scrabble Mini Logo" />
              </Link>
              <Link to={lp('/docs/strategy')} className="hover:text-validator-text transition-colors duration-200">
                <span className="font-bold text-md text-secondary hidden sm:inline">{t('taglineLong')}</span>
                <span className="font-bold text-md text-secondary sm:hidden">{t('taglineShort')}</span>
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            <Link to={lp('/lookup')} className="text-secondary hover:text-validator-text font-medium">{t('navValidator')}</Link>
            <Link to={lp('/word-finder')} className="text-secondary hover:text-validator-text font-medium">{t('navFinder')}</Link>
            <Link to={lp('/anagram-solver')} className="text-secondary hover:text-validator-text font-medium">{t('navAnagram')}</Link>
            <Link to={lp('/magic')} className="text-magic-text hover:text-magic-hover font-medium flex items-center gap-1">
              <span>✨</span>{t('navMagic')}
            </Link>
            <Link to={lp('/practice')} className="text-practice-text hover:text-practice-hover font-medium">{t('navPractice')}</Link>
            <Link to={lp('/docs/readme')} className="text-secondary hover:text-validator-text font-medium">{t('navDocs')}</Link>
            <ThemeToggle />
            <div className="flex items-center">
              <label className="sr-only" htmlFor="language-select">{t('languageLabel')}</label>
              <select
                id="language-select"
                value={locale}
                onChange={(e) => onChangeLocale(e.target.value === 'es' ? 'es' : 'en')}
                className="bg-input-bg border border-input-border rounded-md px-2 py-1 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-validator"
              >
                <option value="en">{t('languageEnglish')}</option>
                <option value="es">{t('languageSpanish')}</option>
              </select>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-muted hover:text-secondary hover:bg-hover focus:outline-none focus:ring-2 focus:ring-inset focus:ring-validator"
            >
              <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to={lp('/lookup')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-secondary hover:bg-hover">{t('navValidator')}</Link>
            <Link to={lp('/word-finder')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-secondary hover:bg-hover">{t('navFinder')}</Link>
            <Link to={lp('/anagram-solver')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-secondary hover:bg-hover">{t('navAnagram')}</Link>
            <Link to={lp('/magic')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-magic-text hover:bg-hover">✨ {t('navMagic')}</Link>
            <Link to={lp('/practice')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-practice-text hover:bg-hover">{t('navPractice')}</Link>
            <Link to={lp('/docs/readme')} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-secondary hover:bg-hover">{t('navDocs')}</Link>
            <div className="px-3 py-2">
              <label className="block text-sm font-medium text-muted mb-1" htmlFor="language-select-mobile">{t('languageLabel')}</label>
              <select
                id="language-select-mobile"
                value={locale}
                onChange={(e) => onChangeLocale(e.target.value === 'es' ? 'es' : 'en')}
                className="w-full bg-input-bg border border-input-border rounded-md px-3 py-2 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-validator"
              >
                <option value="en">{t('languageEnglish')}</option>
                <option value="es">{t('languageSpanish')}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
